'use client'

;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import type { ReactNode } from 'react'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  MONTH_ACTIVITIES,
  monthAssignee,
  teamMember,
  weekdayIndexOf,
  WORKFLOW_STATUS,
  type WorkflowStatusKey,
} from '../demo-data'
import { useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, ColumnLabel, fieldClasses, FieldLabel, HeaderStat } from './app-screen'
import styles from './planning-hours-visual.module.css'
import { useScenario } from './use-scenario'

/** The client whose monthly bookkeeping this card walks through, and the job itself. */
const SUBJECT_NR = '10010'
const SUBJECT = client(SUBJECT_NR)
const ACTIVITY = MONTH_ACTIVITIES[0]

/** Whoever the month board says does this client's bookkeeping — not a name picked here. */
const HANDLER = monthAssignee(SUBJECT_NR, ACTIVITY.key)
/** The accountant who reviews it, and so the colleague the closing comment mentions. */
const REVIEWER = teamMember('MV')

const STATUS_ROWS = WORKFLOW_STATUS
const checklist = (t: VisualCopy) => t.content.bookkeepingChecklist

const BOOKED_HOURS = '1:30'
const DAY_BEFORE = '6:30'
const DAY_AFTER = '8:00'

/**
 * How long each beat holds. The dialog and the booking are read; the checklist ticks at the pace of
 * someone ticking boxes; and the three beats the confirmation stays up are hold time rather than
 * three separate events.
 */
const BEAT_HOLDS = [
  850, //  0  the checklist starts ticking
  800, //  1
  800, //  2
  800, //  3  and the status steps to "Gereed voor controle" as it nears the end
  800, //  4  the last box fills
  1600, // 5  the status follows it to "Afgerond" and the "hoeveel uur?" dialog opens with it
  800, //  6  the hours type in
  1200, // 7  Geboekt ✓ — the payoff
  900, //  8  the dialog closes, the booking lands, the day total ticks up
  900, //  9  the closing comment types
  700, //  10 sent
  1200, // 11 the mention confirmation arrives
  500, //  12 held
  500, //  13 held
  1800, // 14 the finished card — the resting frame
  500, //  15 the wrap
] as const

const REST_BEAT = 14 // SSR / reduced-motion snapshot — Afgerond, all checks done, comment sent, booking present
const RESET_BEAT = 15 // final loop beat: already reads as the sparse starting state

const STATUS_STEP_AT = 3 // status steps to "Gereed voor controle"
// One beat after the last box fills, not on it: a workflow is finished because its checklist is.
// The dialog opens on the same beat, because being marked finished is what asks the question — the
// two are one event, and giving them a beat each put a pause between a cause and its effect.
const STATUS_DONE_AT = 5
const MODAL_FROM = 5 // "Werkzaamheid afgerond — hoeveel uur?" opens with it
const UUR_TYPED_AT = 6 // uur field fills in
const BOEKT_AT = 7 // "Boeken" -> "Geboekt ✓"
const MODAL_TO = 8 // popup closes; booking row lands; day total ticks up
const OPMERKING_TYPED_AT = 9
const VERZONDEN_AT = 10
const NOTIFY_FROM = 11
const NOTIFY_TO = 14

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive card state;
 *  the final loop beat already reads as the sparse starting state, for a seamless wrap. */
function effectiveBeat(beat: number | null): number {
  if (beat === null) return REST_BEAT
  return beat >= RESET_BEAT ? 0 : beat
}

/**
 * The last four items tick off one beat apart, and the status follows them: "Gereed voor controle"
 * as the list nears its end, and "Afgerond" on the beat after the last box fills — a workflow is
 * finished because its checklist is, so the two cannot land together.
 *
 * The list used to finish on beat 2, which left beats 3 and 4 changing nothing but the status — and
 * on a phone the status column is not drawn at all, so those two beats were a card sitting still
 * for two seconds with no way of knowing why. Ticking through them gives every beat something to
 * show at both widths, and it is the better story besides: the status moves because the work does.
 */
function checkedCount(beat: number, total: number): number {
  return Math.min(Math.max(total - 4 + beat, 0), total)
}

function activeStatusKey(beat: number): WorkflowStatusKey {
  if (beat >= STATUS_DONE_AT) return 'afgerond'
  if (beat >= STATUS_STEP_AT) return 'controle'
  return 'uitvoering'
}

/** Decimal hours as the app writes them: 1.2 -> "1:12". */
function hoursLabel(hours: number): string {
  const whole = Math.floor(hours)
  return `${whole}:${String(Math.round((hours - whole) * 60)).padStart(2, '0')}`
}

/** One status radio row. Only the active (current) status is filled + bold. */
function StatusRadio({ label, color, active }: { label: string; color: string; active: boolean }) {
  return (
    <div className="flex min-h-0 flex-1 items-center gap-2">
      <span
        className={clsx('size-[11px] shrink-0 rounded-full', styles.statusDot, active && styles.statusDotActive)}
        style={{ '--dot': color } as React.CSSProperties}
      />
      <span
        className={clsx('truncate text-[11px]', styles.statusLabel)}
        style={{ color: active ? 'var(--au-text)' : 'var(--au-text2)', fontWeight: active ? 700 : 500 }}
      >
        {label}
      </span>
    </div>
  )
}

/** One checklist row; checked rows get an accent check box + muted, struck-through label. */
function ChecklistRow({ label, checked }: { label: string; checked: boolean }) {
  return (
    <div className="flex min-h-0 flex-1 items-center gap-2">
      <span
        className={clsx(
          'flex size-[14px] shrink-0 items-center justify-center rounded-[4px] text-[8.5px] font-bold text-white',
          styles.checkbox,
          checked && styles.checkboxChecked,
        )}
      >
        <span className={clsx(styles.checkGlyph, checked && styles.checkGlyphVisible)}>✓</span>
      </span>
      <span className={clsx('truncate text-[10.5px]', styles.checklistLabel, checked && styles.checklistLabelDone)}>
        {label}
      </span>
    </div>
  )
}

/** One "label — value" line in the left column's detail block. */
function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <FieldLabel>{label}</FieldLabel>
      <span className="flex min-w-0 items-center gap-1.5 text-[10px] font-medium" style={{ color: 'var(--au-text)' }}>
        {children}
      </span>
    </div>
  )
}

/**
 * Afvinken section — the last checklist items tick, the status steps to "Afgerond", an hours popup
 * books the work, the booking lands on the calendar with the day total ticking up, a comment
 * mentioning the reviewing accountant is sent, and a small notification confirms it — then resets.
 * Driven by `useScenario`'s 16-beat loop; `beat === null` (SSR, no-JS, reduced motion) renders the
 * resolved rest state: Afgerond, all checks done, comment sent, booking present, day total 8:00.
 */
export function PlanningHoursVisual({ className }: { className?: string }) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)

  const checked = checkedCount(effBeat, checklist(t).length)
  const activeKey = activeStatusKey(effBeat)
  const modalOpen = effBeat >= MODAL_FROM && effBeat < MODAL_TO
  const uur = effBeat >= UUR_TYPED_AT ? BOOKED_HOURS : undefined
  const boeken = effBeat >= BOEKT_AT
  const filled = effBeat >= MODAL_TO
  const opmerkingTyped = effBeat >= OPMERKING_TYPED_AT
  const verzonden = effBeat >= VERZONDEN_AT
  const notifyVisible = effBeat >= NOTIFY_FROM && effBeat < NOTIFY_TO

  return (
    <AppUiVisual label={t.screen.planningHours.tickIsBooked} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="planning-hours" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            /* The short form in the header. The card is titled by the activity it is about, and
               "Financiële administratie" spends a third of that strip on a word the screen around
               it already says; the full name still names the bookings in the rows below, where it
               identifies work rather than the screen. */
            title={t.monthActivity.administratie.label}
            detail={SUBJECT.name}
            right={
              <span className={styles.headerSlot}>
                <span className={clsx(styles.headerStat, notifyVisible && styles.headerStatOut)}>
                  <HeaderStat value={`${checked}/${checklist(t).length}`} label={t.screen.planningHours.ticked} />
                </span>
                {/* The colleague who was mentioned, in their own colour, the way every other person
                    on this site appears. A generic green tick said "something succeeded"; an avatar
                    says who it reached, which is the thing the mention was for. */}
                <span className={clsx(styles.mention, notifyVisible && styles.mentionIn)}>
                  <span className={styles.mentionAvatar} style={{ background: REVIEWER.color }}>
                    {REVIEWER.initials}
                  </span>
                  <span className="flex min-w-0 flex-col gap-px text-left">
                    <span className={styles.mentionName}>{REVIEWER.name}</span>
                    <span className={styles.mentionMeta}>{t.screen.planningHours.newEntry}</span>
                  </span>
                  <span className={styles.mentionTick} aria-hidden="true">
                    ✓
                  </span>
                </span>
              </span>
            }
          />

          {/* Both columns hand their rows `flex-1`, as the capacity chart does, so the statuses and
              the checklist spread across the card instead of bunching under the header. */}
          <div className={clsx(styles.columns, 'relative min-h-0 flex-1')}>
            <div className={clsx(styles.statusCol, 'px-3 py-2.5')}>
              <ColumnLabel>{t.word.status}</ColumnLabel>
              <div className="flex min-h-0 flex-1 flex-col">
                {STATUS_ROWS.map((row) => (
                  <StatusRadio
                    key={row.key}
                    label={t.workflow[row.key]}
                    color={row.color}
                    active={row.key === activeKey}
                  />
                ))}
              </div>
              {/* Who and when — the detail a reviewer looks for, and the space the statuses alone
                  left empty. */}
              <div className="mt-2 flex flex-col gap-1.5 border-t pt-2" style={{ borderColor: 'var(--au-hairline)' }}>
                <MetaRow label={t.word.owner}>
                  <span
                    className="flex size-[16px] shrink-0 items-center justify-center rounded-full text-[7.5px] font-bold text-white"
                    style={{ background: HANDLER.color }}
                  >
                    {HANDLER.initials}
                  </span>
                  <span className="truncate">{HANDLER.name}</span>
                </MetaRow>
                <MetaRow label={t.word.period}>{t.period.month}</MetaRow>
                <MetaRow label={t.status.gepland}>{hoursLabel(ACTIVITY.avgHours)}</MetaRow>
              </div>
            </div>

            <div className="flex min-h-0 flex-col px-3 py-2.5">
              <ColumnLabel>{t.word.checklist}</ColumnLabel>
              <div className="flex min-h-0 flex-1 flex-col">
                {checklist(t).map((label, i) => (
                  <ChecklistRow key={label} label={label} checked={checked > i} />
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 border-t px-3 py-2.5" style={{ borderColor: 'var(--au-hairline)' }}>
            {/* The roomy field scale, as the popup's hours input uses: this is the one thing on the
                card a visitor is meant to read as an input they could type in. Its size comes from
                that class rather than a utility beside it — the module rule wins either way, so
                setting it twice only hides which value is really in effect. */}
            <div
              className={clsx(
                fieldClasses.box,
                fieldClasses.boxRoomy,
                'min-w-0 flex-1',
                opmerkingTyped && fieldClasses.filled,
              )}
              style={{ color: opmerkingTyped ? 'var(--au-text)' : 'var(--au-text2)' }}
            >
              {opmerkingTyped ? (
                <>
                  <span
                    className="rounded-[4px] px-1.5 py-[1px] font-bold"
                    style={{
                      background: 'color-mix(in oklab, var(--au-sig) 18%, transparent)',
                      color: 'var(--au-sig)',
                    }}
                  >
                    @{REVIEWER.firstName}
                  </span>{' '}
                  {t.content.hoursRemark}
                </>
              ) : (
                t.screen.planningHours.addComment
              )}
            </div>
            <span className={clsx(styles.button, verzonden && styles.buttonDone)}>
              {verzonden ? t.screen.planningHours.sent : t.screen.planningHours.send}
            </span>
          </div>

          <div className="flex flex-col gap-1.5 border-t px-3 py-2.5" style={{ borderColor: 'var(--au-hairline)' }}>
            <div className="flex items-center justify-between gap-2">
              <ColumnLabel>
                {t.screen.hours.title} · {fmt.weekdayDate(15, weekdayIndexOf(15))}
              </ColumnLabel>
              <span className="shrink-0 text-[10px] whitespace-nowrap" style={{ color: 'var(--au-text2)' }}>
                {t.word.today}{' '}
                <b className="tabular-nums" style={{ color: 'var(--au-accent)' }}>
                  {filled ? DAY_AFTER : DAY_BEFORE}
                </b>
              </span>
            </div>
            {/* The row keeps its height whether or not it holds a booking: an empty placeholder
                waits there and the booking lands in it. Growing the strip from nothing pushed the
                whole card taller at that beat, so everything above it shifted while the viewer was
                reading it. */}
            <div className={styles.bookingSlot}>
              <div
                className={clsx(styles.bookingPlaceholder, filled && styles.bookingPlaceholderGone)}
                style={{ borderColor: 'var(--au-border)' }}
                aria-hidden="true"
              />
              <div
                className={clsx(
                  'flex items-center justify-between gap-2 rounded-[6px] border px-2.5 py-2',
                  styles.bookingInner,
                  filled && styles.bookingInnerVisible,
                )}
                style={{ borderColor: 'var(--au-border)', background: 'var(--au-surface)' }}
              >
                {/* The short name, as the signal list uses: the header above already carries the
                    full legal name, and at phone width the full one crowded out the activity. */}
                <span className="min-w-0 flex-1 truncate text-[10px] font-medium" style={{ color: 'var(--au-text)' }}>
                  {SUBJECT.short}{' '}
                  <span style={{ color: 'var(--au-text2)', fontWeight: 500 }}>
                    · {t.monthActivity.administratie.fullLabel}
                  </span>
                </span>
                <div className="flex shrink-0 items-center gap-2">
                  <b className="text-[11px] tabular-nums" style={{ color: 'var(--au-text)' }}>
                    {BOOKED_HOURS}
                  </b>
                  <span
                    className={clsx(
                      styles.billable,
                      'rounded-[4px] border px-1.5 py-[2px] text-[7.5px] font-bold whitespace-nowrap uppercase',
                    )}
                    style={{ borderColor: 'var(--au-done)', color: 'var(--au-done)', letterSpacing: '0.04em' }}
                  >
                    Facturabel
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={clsx(styles.scrim, modalOpen && styles.scrimVisible)} data-overlay-scrim aria-hidden="true">
          <div
            className={clsx(styles.modal, 'w-[80%] max-w-[250px] rounded-[9px] border shadow-sm')}
            data-overlay-panel
            style={{ background: 'var(--au-surface)', borderColor: 'var(--au-border)' }}
          >
            <div className="border-b px-3 py-2.5" style={{ borderColor: 'var(--au-hairline)' }}>
              <div className="text-[11.5px] font-semibold" style={{ color: 'var(--au-text)' }}>
                {t.screen.planningHours.completed}
              </div>
              <div className="text-[9px]" style={{ color: 'var(--au-text2)' }}>
                {t.monthActivity.administratie.fullLabel} · {SUBJECT.short}
              </div>
            </div>
            <div className="flex flex-col gap-2 px-3 py-2.5">
              <div className="flex flex-col gap-0.5">
                <FieldLabel roomy>{t.screen.planningHours.spentHours}</FieldLabel>
                <div
                  className={clsx(
                    fieldClasses.box,
                    fieldClasses.boxRoomy,
                    'text-right text-[12.5px] font-bold',
                    uur && fieldClasses.filled,
                  )}
                  style={{ color: uur ? 'var(--au-accent)' : 'var(--au-text2)' }}
                >
                  {uur ?? '—'}
                </div>
              </div>
              <div className="flex justify-end">
                <span className={clsx(styles.button, boeken && styles.buttonDone)}>
                  {boeken ? t.screen.jaarplanning.booked : t.screen.hours.book}
                </span>
              </div>
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
